import React from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, Modal, toast, useWizard } from "@picklebet/ui";
import { constructCurrency } from "common/currency";
import { parseError } from "@/utilities/errorsParser";
import { useWithdrawAmountValidationSchema } from "@/features/wallet/hooks/useWithdrawAmountValidationSchema";
import { useGameSessions } from "@/features/casino/hooks/useGameSessions";
import { SupportLink } from "@/features/shell/components/shared/SupportLink";
import { getInteracBankAccountLabel } from "@/features/wallet/utils/interac";
import {
  MethodRow,
  MethodRowSkeleton,
} from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/MethodRow";
import { BankMethodIcon } from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/BankMethodIcon";
import { OptionRowAdd } from "@/features/wallet/components/deposit/components/deposit-wizard/option-row/OptionAddRow";
import { AmountInputSkeleton } from "@/features/wallet/components/shared/amount-input-skeleton/AmountInputSkeleton";
import { useReportSubmitting } from "@/features/wallet/hooks/useReportSubmitting";
import { useWithdrawWizard } from "@/features/wallet/components/withdraw/hooks/useWithdrawWizard";
import { useInteracWithdrawal } from "@/features/wallet/components/withdraw/hooks/useInteracWithdrawal";
import { useWithdrawalWindowAlertDismissed } from "@/features/wallet/components/withdraw/hooks/useWithdrawalWindowAlertDismissed";
import type { WithdrawStepName } from "@/features/wallet/components/withdraw/utils/registry";
import { WithdrawContent } from "../../blocks/withdraw-content/WithdrawContent";
import { WithdrawAmountInput } from "../../blocks/withdraw-amount-input/WithdrawAmountInput";
import {
  WithdrawableBalance,
  WithdrawableBalanceSkeleton,
} from "../../blocks/withdrawable-balance/WithdrawableBalance";
import { ActiveSessionAlert } from "../../blocks/active-session-alert/ActiveSessionAlert";
import { SectionDivider } from "../../blocks/section-divider/SectionDivider";
import { SectionHeader } from "../../blocks/section-header/SectionHeader";

import * as styles from "./InteracWithdrawView.module.scss";

// mirrors the settled view block for block: any row missing here becomes a
// jump in modal height when the data lands
export const InteracWithdrawViewSkeleton = () => (
  <WithdrawContent>
    <AmountInputSkeleton />
    <SectionDivider />
    <SectionHeader>Interac e-Transfer</SectionHeader>
    <MethodRowSkeleton />
    <Modal.Footer>
      <Button className={styles.withdrawButton} skeleton>
        Withdraw
      </Button>
    </Modal.Footer>
    <WithdrawableBalanceSkeleton />
  </WithdrawContent>
);

export const InteracWithdrawView = () => {
  const {
    amount,
    setAmount,
    currency,
    setSubmitting,
    mustVerifyToWithdraw,
    showVerificationRequired,
    canWithdraw,
    showWithdrawUnavailable,
  } = useWithdrawWizard();
  const { goTo } = useWizard<WithdrawStepName>();
  const { hasActiveGameSession } = useGameSessions(true);
  const {
    submit,
    selectedAddress,
    isLoading: addressesLoading,
    error: addressesError,
  } = useInteracWithdrawal();

  const [schema, schemaLoading] = useWithdrawAmountValidationSchema();
  const [alertDismissed, setAlertDismissed] =
    useWithdrawalWindowAlertDismissed();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { amount },
  });

  useReportSubmitting(isSubmitting, setSubmitting);

  // checked ahead of the amount validation so a blocked user is told why
  // rather than shown an amount error
  const onFormSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    if (mustVerifyToWithdraw) {
      event.preventDefault();
      showVerificationRequired();
      return;
    }

    if (!canWithdraw) {
      event.preventDefault();
      showWithdrawUnavailable();
      return;
    }

    return handleSubmit(onSubmit)(event);
  };

  const onSubmit = async (values: { amount: number }) => {
    const result = await submit(values.amount);

    if (!result.ok) {
      toast({
        variant: "danger",
        title: "Withdrawal Failed",
        description: parseError(result.error),
      });
    }
  };

  if (schemaLoading || addressesLoading) {
    return <InteracWithdrawViewSkeleton />;
  }

  return (
    <form onSubmit={onFormSubmit}>
      <WithdrawContent>
        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, value } }) => (
            <WithdrawAmountInput
              value={value}
              onChange={(next) => {
                onChange(next);
                setAmount(next);
              }}
              errorMessage={errors.amount?.message}
            />
          )}
        />

        <SectionDivider />

        <SectionHeader>Interac e-Transfer</SectionHeader>

        {addressesError ? (
          <Alert color="danger" size="small">
            We couldn't load your Interac details, please refresh and try again
            or contact{" "}
            <SupportLink openLiveChat hideChevron>
              Customer Support
            </SupportLink>
            .
          </Alert>
        ) : selectedAddress ? (
          <MethodRow
            icon={<BankMethodIcon />}
            label={getInteracBankAccountLabel(selectedAddress)}
            trailing={
              <Button
                size="sm"
                onClick={() => goTo("withdraw/interac-account")}
              >
                Change
              </Button>
            }
          />
        ) : (
          <OptionRowAdd
            label="Add Interac Details"
            onClick={() => goTo("withdraw/interac-account/add")}
          />
        )}

        <ActiveSessionAlert />

        <Modal.Footer>
          <Button
            className={styles.withdrawButton}
            type="submit"
            disabled={
              !amount ||
              !selectedAddress ||
              hasActiveGameSession ||
              isSubmitting
            }
            loading={isSubmitting}
          >
            Withdraw{" "}
            {constructCurrency(amount * 100, { currencyType: currency })}
          </Button>
        </Modal.Footer>

        <WithdrawableBalance />

        {!alertDismissed && (
          <Alert
            variant="info"
            size="small"
            onDismiss={() => setAlertDismissed(true)}
          >
            Withdrawals are processed multiple times per day, 7 days a week.
          </Alert>
        )}
      </WithdrawContent>
    </form>
  );
};
